/**
 * Countdown elements for the "coming soon" product experience.
 *
 * <countdown-timer data-launch-date="2026-09-07T10:00:00+1000">
 *   Flip-digit countdown: days / hours / mins / secs.
 *
 * <launch-date data-launch-date="...">
 *   The launch moment restated in the shopper's own timezone.
 *
 * Both render nothing once the launch date has passed, so a stale cached page
 * degrades to no countdown rather than to a wrong one.
 *
 * The countdown is display only. It never decides whether a product can be
 * bought: local clocks can be changed, and Liquid render time is cached by
 * Shopify. Purchasability is enforced by variant inventory.
 */

(function () {
  'use strict';

  /**
   * One shared ticker drives every mounted timer.
   *
   * A collection page can hold 20+ coming-soon cards. Giving each element its
   * own setInterval means 20+ timers waking independently; instead they
   * subscribe here and are ticked together, which also keeps the digits on all
   * cards visually in sync.
   */
  const ticker = {
    subscribers: new Set(),
    intervalId: null,

    add(element) {
      this.subscribers.add(element);
      if (this.intervalId === null) {
        this.intervalId = setInterval(() => this.tick(), 1000);
      }
    },

    remove(element) {
      this.subscribers.delete(element);
      // Stop the timer entirely once nothing is listening, so the interval does
      // not outlive the elements (e.g. after a section re-render).
      if (this.subscribers.size === 0 && this.intervalId !== null) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },

    tick() {
      const now = Date.now();
      // Copy before iterating: onTick can unsubscribe on expiry.
      Array.from(this.subscribers).forEach((element) => element.onTick(now));
    },
  };

  // Shopify Flow removes the `coming-soon` tag on a schedule, so the tag can
  // still be there for several minutes after the countdown reaches zero. Rather
  // than reveal a buy form the server never rendered — the browser clock must
  // never decide purchasability — the page reloads periodically and lets the
  // server answer. The first reload after Flow runs renders the normal product.
  const RECHECK_INTERVAL_MS = 60000;
  const RECHECK_MAX = 12; // ~12 min, comfortably past Flow's 10-minute cycle.
  const RECHECK_KEY = 'comingSoonRechecks:' + window.location.pathname;

  let recheckScheduled = false;

  function readRecheckCount() {
    try {
      return parseInt(sessionStorage.getItem(RECHECK_KEY), 10) || 0;
    } catch (error) {
      // Private mode or blocked storage: no counter means no bounded retry, so
      // do not reload at all rather than risk an endless cycle.
      return null;
    }
  }

  /** Clears the counter so the next launch starts from a full retry budget. */
  function resetLaunchRecheck() {
    try {
      sessionStorage.removeItem(RECHECK_KEY);
    } catch (error) {
      /* nothing to reset */
    }
  }

  /** Queues one reload. Several elements expire together; only the first wins. */
  function scheduleLaunchRecheck() {
    if (recheckScheduled) return;

    const count = readRecheckCount();
    if (count === null || count >= RECHECK_MAX) return;

    recheckScheduled = true;
    setTimeout(() => {
      try {
        sessionStorage.setItem(RECHECK_KEY, String(count + 1));
      } catch (error) {
        return; // Cannot count the attempt, so do not spend it.
      }
      window.location.reload();
    }, RECHECK_INTERVAL_MS);
  }

  /**
   * Parses the launch date and returns its epoch ms, or null when the value is
   * missing or unparseable. Callers treat null as "not coming soon".
   */
  function parseLaunchDate(value) {
    if (!value) return null;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
  }

  function splitDuration(ms) {
    return {
      days: Math.floor(ms / 86400000),
      hours: Math.floor((ms % 86400000) / 3600000),
      minutes: Math.floor((ms % 3600000) / 60000),
      seconds: Math.floor((ms % 60000) / 1000),
    };
  }

  if (!customElements.get('countdown-timer')) {
    class CountdownTimer extends HTMLElement {
      connectedCallback() {
        this.launchTime = parseLaunchDate(this.dataset.launchDate);
        if (this.launchTime === null) return;

        if (this.launchTime <= Date.now()) {
          this.showLaunching();
          return;
        }

        // Still ahead of launch: this is a fresh cycle, so retire any retry
        // budget spent on a previous one.
        resetLaunchRecheck();

        this.labels = {
          days: this.dataset.labelDays || 'Days',
          hours: this.dataset.labelHours || 'Hours',
          minutes: this.dataset.labelMins || 'Mins',
          seconds: this.dataset.labelSecs || 'Secs',
        };

        this.render();
        this.onTick(Date.now());
        ticker.add(this);
      }

      disconnectedCallback() {
        ticker.remove(this);
      }

      onTick(now) {
        const remaining = this.launchTime - now;

        if (remaining <= 0) {
          ticker.remove(this);
          this.showLaunching();
          return;
        }

        const time = splitDuration(remaining);
        this.updateUnit('days', time.days);
        this.updateUnit('hours', time.hours);
        this.updateUnit('minutes', time.minutes);
        this.updateUnit('seconds', time.seconds);

        this.setAttribute(
          'aria-label',
          `${time.days} ${this.labels.days}, ${time.hours} ${this.labels.hours}, ` +
            `${time.minutes} ${this.labels.minutes}, ${time.seconds} ${this.labels.seconds}`
        );
      }

      /**
       * Replaces the digits with a holding message and starts asking the server
       * whether the product has launched. Reached both by ticking down to zero
       * and by mounting on an already-expired page.
       */
      showLaunching() {
        this.textContent = this.dataset.labelLaunching || '';
        this.removeAttribute('aria-label');
        this.classList.add('countdown--launching');
        scheduleLaunchRecheck();
      }

      updateUnit(name, value) {
        const unit = this.querySelector(`[data-unit="${name}"]`);
        if (!unit) return;

        // Days can exceed 99, so clamp the tens column rather than dropping the
        // hundreds digit silently.
        const capped = Math.min(value, 99);
        this.updateDigit(unit.querySelector('[data-digit="tens"]'), Math.floor(capped / 10));
        this.updateDigit(unit.querySelector('[data-digit="ones"]'), capped % 10);
      }

      updateDigit(container, number) {
        if (!container) return;
        container.querySelectorAll('[data-number]').forEach((el) => {
          el.dataset.visible = Number(el.dataset.number) === number ? 'true' : 'false';
        });
      }

      render() {
        this.innerHTML = `
          <div class="countdown" role="timer">
            ${this.unit('days', this.labels.days)}
            ${this.separator()}
            ${this.unit('hours', this.labels.hours)}
            ${this.separator()}
            ${this.unit('minutes', this.labels.minutes)}
            ${this.separator()}
            ${this.unit('seconds', this.labels.seconds)}
          </div>
        `;
      }

      separator() {
        return '<span class="countdown__separator" aria-hidden="true">:</span>';
      }

      unit(key, label) {
        return `
          <div class="countdown__unit" data-unit="${key}">
            <div class="countdown__digits">
              ${this.digit('tens')}
              ${this.digit('ones', true)}
            </div>
            <span class="countdown__label">${label}</span>
          </div>
        `;
      }

      digit(position, offset = false) {
        const numbers = Array.from(
          { length: 10 },
          (_, i) => `<span data-number="${i}" class="countdown__number" aria-hidden="true">${i}</span>`
        ).join('');

        return `
          <span class="countdown__digit${offset ? ' is-offset' : ''}" data-digit="${position}">
            ${numbers}
          </span>
        `;
      }
    }

    customElements.define('countdown-timer', CountdownTimer);
  }

  /**
   * <launch-date data-launch-date="..." data-template="Available {{ date }}">
   *
   * Reformats the launch date into the visitor's own timezone. Liquid always
   * renders dates in the shop's timezone, so an AU-hosted store would tell a
   * New Zealand shopper the wrong local time; only the browser knows where the
   * visitor actually is.
   *
   * Liquid still renders a server-side value inside the element, so the date is
   * present without JS — this only upgrades it.
   */
  if (!customElements.get('launch-date')) {
    class LaunchDate extends HTMLElement {
      connectedCallback() {
        const launchTime = parseLaunchDate(this.dataset.launchDate);
        if (launchTime === null) return;

        // Past the launch moment "Available <yesterday>" reads as nonsense, and
        // the countdown above already says "Launching now" — repeating it here
        // would just say the same thing twice. Step aside instead.
        if (launchTime <= Date.now()) {
          this.hidden = true;
          scheduleLaunchRecheck();
          return;
        }

        // Shopify's locale is a bare language ("en") with no region, which
        // Intl treats as US English: "August 31 ... GMT+10" rather than
        // "31 August ... AEST". Pair it with the market's country so AU and NZ
        // shoppers get their own conventions.
        const language = this.dataset.locale || 'en';
        const country = this.dataset.country || (window.Shopify && window.Shopify.country);
        const locale = country ? `${language}-${country}` : language;

        // The visitor's own IANA zone, e.g. "Australia/Melbourne". Read from
        // the browser rather than mapped from the market, so new markets need
        // no timezone table to be maintained here.
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!zone) return;

        // Format: "04/08/2026 at 10:00 (Australia/Melbourne)". Date and time
        // are formatted separately so the IANA zone name can be spelled out in
        // full — Intl's own timeZoneName only offers abbreviations ("AEST").
        const date = new Date(launchTime);
        let formatted;

        try {
          const day = new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: zone,
          }).format(date);

          const time = new Intl.DateTimeFormat(locale, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: zone,
          }).format(date);

          formatted = `${day} ${this.dataset.at || 'at'} ${time} (${zone})`;
        } catch (error) {
          // Unsupported locale or zone: keep whatever the server rendered.
          return;
        }

        const label = this.dataset.label;
        this.textContent = label ? `${label} ${formatted}` : formatted;
      }
    }

    customElements.define('launch-date', LaunchDate);
  }
})();
